const ORIGINAL_SEGMENTS_KEY = "speaker_postprocessing_original_segments";
const REVIEW_META_KEY = "high_quality_speaker_annotation";

const defaultLabels = {
  detectedLanguage: "Detected Language",
  noTranscription: "No transcription available",
  shortSummary: "Short summary",
  summary: "Summary",
  summaryPending: "Summary is still being prepared.",
  noSummary: "No summary available for this transcript.",
};

const label = (labels, key) => labels?.[key] || defaultLabels[key] || key;

export function formatTimestamp(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "00:00";
  const totalSec = Math.floor(Number(seconds));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const cleanText = (value) => String(value || "").trim();

const joinTranscriptWords = (words) => (
  words
    .map((word) => cleanText(word?.word))
    .filter(Boolean)
    .join(" ")
    .replace(/\s+([,.;:!?%])/g, "$1")
    .trim()
);

const speakerDisplayNamesFromFull = (full) => {
  const meta = full?.diarization?.[REVIEW_META_KEY] || {};
  const names = meta.status === "applied" ? { ...meta.speaker_display_names } : {};
  const overrides = full?.speaker_name_overrides || {};
  const aliases = meta.speaker_merge_aliases || {};
  Object.entries(overrides).forEach(([id, name]) => {
    names[id] = name;
    if (!overrides[aliases[id]]) names[aliases[id] || id] = name;
  });
  Object.entries(aliases).forEach(([id, canonical]) => {
    if (overrides[canonical]) names[id] = overrides[canonical];
  });
  return names;
};

export function getTranscriptSegments(data, usePostprocessing = true) {
  if (!data) return [];
  const original = data.light_segments_original || data.full?.[ORIGINAL_SEGMENTS_KEY] || [];
  const useOriginal = !usePostprocessing && original.length > 0;
  const names = speakerDisplayNamesFromFull({
    ...(data.full || {}),
    diarization: { [REVIEW_META_KEY]: useOriginal ? {} : getSpeakerPostprocessingMeta(data) || {} },
    speaker_name_overrides: data.speaker_name_overrides || data.full?.speaker_name_overrides || {},
  });
  const segments = useOriginal ? original : data.light_segments || data.full?.segments || data.segments || [];
  // Rich segments have raw speaker IDs; light segments retain speaker_id so
  // names never become identities and equal display names never merge turns.
  const light = segments.some(segment => segment?.words?.length)
    ? segmentsToLightSegments(segments, names)
    : segments;
  return light.map(segment => {
    const id = segment.speaker_id || segment.speaker;
    return { ...segment, speaker_id: id, speaker: names[id] || segment.speaker };
  });
}

export function getTranscriptSpeakers(data, usePostprocessing = true) {
  const options = new Map((data?.speaker_options || []).map(speaker => [speaker.id, speaker]));
  const speakers = new Map();
  for (const segment of getTranscriptSegments(data, usePostprocessing)) {
    const id = segment.speaker_id || segment.speaker;
    if (!id || speakers.has(id)) continue;
    const option = options.get(id) || {};
    speakers.set(id, { ...option, id, name: segment.speaker || id });
  }
  return [...speakers.values()];
}

const displaySpeakerLabel = (speaker, speakerDisplayNames = {}) => {
  if (speaker == null) return null;
  const speakerLabel = String(speaker);
  return speakerDisplayNames[speakerLabel] || speakerLabel;
};

export function segmentsToLightSegments(segments, speakerDisplayNames = {}) {
  const lightSegments = [];
  (segments || []).forEach((segment) => {
    const words = (segment?.words || []).filter((word) => cleanText(word?.word));

    if (words.length && words.some((word) => word?.speaker || segment?.speaker)) {
      let currentSpeaker = null;
      let currentWords = [];

      const flushCurrent = () => {
        if (!currentWords.length) return;
        const item = { text: joinTranscriptWords(currentWords) };
        const first = currentWords[0];
        const last = currentWords[currentWords.length - 1];
        if (first?.start != null) item.start = first.start;
        if (last?.end != null) item.end = last.end;
        if (currentSpeaker) {
          item.speaker_id = currentSpeaker;
          item.speaker = displaySpeakerLabel(currentSpeaker, speakerDisplayNames);
        }
        if (item.text) lightSegments.push(item);
      };

      words.forEach((word) => {
        const speaker = word?.speaker || segment?.speaker;
        if (currentWords.length && speaker !== currentSpeaker) {
          flushCurrent();
          currentWords = [];
        }
        currentSpeaker = speaker;
        currentWords.push(word);
      });
      flushCurrent();
      return;
    }

    const item = { text: cleanText(segment?.text) };
    if (segment?.start != null) item.start = segment.start;
    if (segment?.end != null) item.end = segment.end;
    if (segment?.speaker != null) {
      item.speaker_id = segment.speaker_id || segment.speaker;
      item.speaker = displaySpeakerLabel(item.speaker_id, speakerDisplayNames);
    }
    if (item.text) lightSegments.push(item);
  });
  return lightSegments;
}

export function getSpeakerPostprocessingMeta(data) {
  return (
    data?.speaker_postprocessing ||
    data?.full?.diarization?.[REVIEW_META_KEY] ||
    null
  );
}

export function isSpeakerPostprocessingRunning(data) {
  return getSpeakerPostprocessingMeta(data)?.status === "processing";
}

export function hasSpeakerPostprocessingResult(data) {
  return Boolean(
    data?.speaker_postprocessing_available &&
    (data?.light_segments_original?.length || data?.full?.[ORIGINAL_SEGMENTS_KEY]?.length)
  );
}

export function getTranscriptSummary(data) {
  const summary =
    data?.transcript_summary ||
    data?.full?.diarization?.[REVIEW_META_KEY]?.transcript_summary ||
    null;
  if (!summary || typeof summary !== "object") return null;

  const shortSummary = cleanText(summary.short_summary);
  const longSummary = cleanText(summary.summary);
  return {
    short_summary: shortSummary && shortSummary.length <= 60 ? shortSummary : "",
    summary: longSummary,
  };
}

export function hasTranscriptSummary(data) {
  return Boolean(getTranscriptSummary(data)?.summary);
}

export function getSummaryAvailability(data) {
  if (hasTranscriptSummary(data)) return "ready";
  if (isSpeakerPostprocessingRunning(data)) return "pending";
  return "missing";
}

export function formatSummaryContent(data, labels = defaultLabels) {
  const summary = getTranscriptSummary(data);
  if (summary?.summary) {
    const parts = [];
    if (summary.short_summary) {
      parts.push(`${label(labels, "shortSummary")}:\n${summary.short_summary}`);
    }
    parts.push(`${label(labels, "summary")}:\n${summary.summary}`);
    return parts.join("\n\n");
  }

  if (isSpeakerPostprocessingRunning(data)) {
    return label(labels, "summaryPending");
  }
  return label(labels, "noSummary");
}

export function isTranscriptEmpty(data) {
  if (!data) return false;
  if (data.result_state === 'empty') return true;
  if (data.result_state === 'text') return false;
  if (!/^(completed|succeeded)/i.test(data.status || '')) return false;
  const known = 'transcription_text' in data || 'light_segments' in data || 'segments' in data || 'full' in data;
  if (!known) return false;
  const text = String(data.transcription_text || '').trim();
  return (!text || text === 'No transcription available')
    && !getTranscriptSegments(data).some(segment => String(segment?.text || '').trim());
}

export function formatTranscriptionContent(
  data,
  {
    withSpeakers = false,
    withTime = false,
    usePostprocessing = true,
    labels = defaultLabels,
  } = {}
) {
  if (!data) return label(labels, "noTranscription");

  let content = "";
  if (data.detected_language) {
    content += `${label(labels, "detectedLanguage")}: ${data.detected_language}\n\n`;
  }

  const enhancedSegments = getTranscriptSegments(data, usePostprocessing);

  const canRenderSpeakers = withSpeakers && enhancedSegments.some((segment) => segment?.speaker);
  const canRenderTimes = withTime && enhancedSegments.some((segment) => segment?.start != null);

  if (canRenderSpeakers || canRenderTimes) {
    let lastSpeaker = "";
    enhancedSegments.forEach((segment) => {
      const currentSpeaker = segment?.speaker || "";
      const currentSpeakerId = segment?.speaker_id || currentSpeaker;
      const startTime = segment?.start != null ? formatTimestamp(segment.start) : null;

      if (withSpeakers && currentSpeaker && currentSpeakerId !== lastSpeaker) {
        if (content.trim() !== "" && !content.endsWith("\n\n")) {
          content += "\n\n";
        }
        content += `${currentSpeaker}:\n`;
        lastSpeaker = currentSpeakerId;
      }

      let line = "";
      if (withTime && startTime) {
        line += `[${startTime}] `;
      }
      line += cleanText(segment?.text);
      if (line.trim()) {
        content += `${line}\n`;
      }
    });
    return content;
  }

  return content + (data.transcription_text || data.transcription_result || label(labels, "noTranscription"));
}

export function normalizeTranscriptData(payload) {
  if (!payload) return null;
  if (payload.transcription_text || payload.light_segments || payload.full || payload.segments) {
    return payload;
  }

  const result = payload.result || payload;
  const raw = result.raw_json || result.raw_result || null;
  if (!raw || typeof raw !== "object") {
    return {
      ...payload,
      transcription_text: result.text || result.raw_text || "",
      has_diarization: Boolean(result.has_diarization),
      has_timestamps: false,
      full_available: Boolean(result.has_full),
    };
  }

  const output = raw.output || {};
  const full = raw.full || {};
  const speakerDisplayNames = speakerDisplayNamesFromFull(full);
  const lightSegments = segmentsToLightSegments(full.segments || output.segments || [], speakerDisplayNames);
  const originalLightSegments = segmentsToLightSegments(full[ORIGINAL_SEGMENTS_KEY] || [], full.speaker_name_overrides || {});
  const speakerPostprocessing = getSpeakerPostprocessingMeta({ full });
  const transcriptSummary = getTranscriptSummary({ full });

  return {
    id: payload.id || raw.id,
    original_filename: payload.original_filename || raw.original_filename,
    new_filename: payload.new_filename || raw.new_filename,
    uploaded_time: payload.uploaded_time || raw.uploaded_time,
    duration: payload.duration || raw.duration,
    status: payload.status || raw.status,
    transcription_text: output.transcription || raw.transcription_text || result.text || "",
    detected_language: output.detected_language || result.detected_language || null,
    segments: output.segments || [],
    full,
    speaker_name_overrides: full.speaker_name_overrides || {},
    full_available: Boolean(full && Object.keys(full).length),
    has_diarization: Boolean(
      full?.diarization?.enabled ||
      lightSegments.some((segment) => segment?.speaker)
    ),
    has_timestamps: Boolean(lightSegments.some((segment) => segment?.start != null)),
    speaker_postprocessing_available: Boolean(
      speakerPostprocessing?.status === "applied" && originalLightSegments.length
    ),
    speaker_postprocessing: speakerPostprocessing || undefined,
    transcript_summary: transcriptSummary || undefined,
    short_summary: transcriptSummary?.short_summary || undefined,
    light_segments: lightSegments.length ? lightSegments : undefined,
    light_segments_original: originalLightSegments.length ? originalLightSegments : undefined,
  };
}

export function safeBaseFilename(name, fallback = "transcript") {
  const withoutExt = String(name || fallback).replace(/\.[^/.]+$/, "");
  const cleaned = withoutExt
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  return (cleaned || fallback).slice(0, 120);
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (view, offset, value) => {
  view.setUint16(offset, value, true);
};

const writeUint32 = (view, offset, value) => {
  view.setUint32(offset, value >>> 0, true);
};

const dosDateTime = (date = new Date()) => {
  const year = Math.max(1980, date.getFullYear());
  const dosTime =
    (date.getHours() << 11) |
    (date.getMinutes() << 5) |
    Math.floor(date.getSeconds() / 2);
  const dosDate =
    ((year - 1980) << 9) |
    ((date.getMonth() + 1) << 5) |
    date.getDate();
  return { dosTime, dosDate };
};

const normalizeZipPath = (name) => (
  String(name || "file.txt")
    .replace(/^[./\\]+/g, "")
    .replace(/\\/g, "/")
    .replace(/\/+/g, "/")
);

export async function createZipBlob(entries) {
  const encoder = new TextEncoder();
  const { dosTime, dosDate } = dosDateTime();
  const parts = [];
  const centralParts = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(normalizeZipPath(entry.name));
    const value = entry.data instanceof Blob
      ? new Uint8Array(await entry.data.arrayBuffer())
      : encoder.encode(String(entry.data ?? ""));
    const crc = crc32(value);
    const localHeader = new ArrayBuffer(30 + nameBytes.length);
    const localView = new DataView(localHeader);
    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0x0800);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, dosTime);
    writeUint16(localView, 12, dosDate);
    writeUint32(localView, 14, crc);
    writeUint32(localView, 18, value.length);
    writeUint32(localView, 22, value.length);
    writeUint16(localView, 26, nameBytes.length);
    writeUint16(localView, 28, 0);
    new Uint8Array(localHeader, 30).set(nameBytes);

    parts.push(localHeader, value);

    const centralHeader = new ArrayBuffer(46 + nameBytes.length);
    const centralView = new DataView(centralHeader);
    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0x0800);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, dosTime);
    writeUint16(centralView, 14, dosDate);
    writeUint32(centralView, 16, crc);
    writeUint32(centralView, 20, value.length);
    writeUint32(centralView, 24, value.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, offset);
    new Uint8Array(centralHeader, 46).set(nameBytes);
    centralParts.push(centralHeader);

    offset += localHeader.byteLength + value.length;
  }

  const centralSize = centralParts.reduce((sum, part) => sum + part.byteLength, 0);
  const end = new ArrayBuffer(22);
  const endView = new DataView(end);
  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 4, 0);
  writeUint16(endView, 6, 0);
  writeUint16(endView, 8, entries.length);
  writeUint16(endView, 10, entries.length);
  writeUint32(endView, 12, centralSize);
  writeUint32(endView, 16, offset);
  writeUint16(endView, 20, 0);

  return new Blob([...parts, ...centralParts, end], { type: "application/zip" });
}
